import { 
  IconButton, Table, TableBody, Grid, TableCell, TableContainer, TableHead, 
  TableRow, TextField, useTheme, Box, Typography, FormControl, Divider, Button, 
  Autocomplete, alpha 
} from "@mui/material";
import CustomTableContainer from "../../../components/table/CustomTableContainer";
import SkeletonTableLoader from "../../../components/table/SkeletonTableLoader";
import { useEffect, useState } from "react";

import DownloadIcon from '@mui/icons-material/Download';
import Close from '@mui/icons-material/Close';
import * as XLSX from "xlsx";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import GlobalConfig from "../../../config/date/GlobalConfig";
import dayjs from "dayjs";
import { tokens } from "../../../theme/theme";
import CustomPagination from "../../../components/table/CustomPagination";
import { callApi } from "../../../utils/apiClient";
import { API_ENDPOINTS } from "../../../config/api/apiCall";

// New API function to fetch WIP reel details
const getWIPReelDetails = async (payload) => {
  try {
    const endpoint = API_ENDPOINTS.PROD_MANAGEMENT_REPORT_GET_WINDER_PRODUCTION_WIP_REELS;
    return await callApi(endpoint, "POST", { "Content-Type": "application/json" }, payload);
  } catch (error) {
    console.error("Error fetching WIP reel details:", error);
    throw error;
  }
};

const ReportWinderProuctionWIPReelsList = ({ handleBackWinderMachineEntry, mode }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // State management for data, loading, and filters
  const [wipReelDetails, setWipReelDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [filterFromDate, setFilterFromDate] = useState(dayjs().subtract(GlobalConfig.defaultDays, "day"));
  const [filterToDate, setFilterToDate] = useState(dayjs());
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    prod_date: "",
    shift_code: "",
    roll_no: "",
    reel_no: "",
    quality: "",
    grade: "",
    gsm: "",
    size: "",
    weight: "",
    po_no: "",
    remarks: "",
    party_remarks: "",
  });

  useEffect(() => {
    setCurrentPage(1); // reset to first page when component mounts
  }, []);

  const loadDataWithDate = async (from, to) => {
    setLoading(true);
    try {
      const p_companyid = localStorage.getItem("companyid");
      const payload = {
        p_companyid: p_companyid,
        p_plantid: 1,
        p_frommodate: dayjs(from).format("YYYY-MM-DD HH:mm:ss"),
        p_tomodate: dayjs(to).format("YYYY-MM-DD HH:mm:ss"),
        p_mode: 0,
      };
      const data = await getWIPReelDetails(payload);
      setWipReelDetails(data);
    } catch (error) {
      console.error("Failed to load data with date range:", error);
      setWipReelDetails([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDataWithoutDate = async () => {
    setLoading(true);
    try {
      const p_companyid = localStorage.getItem("companyid");
      const payload = {
        p_companyid: p_companyid,
        p_plantid: 1,
        p_mode: 0,
      };
      const data = await getWIPReelDetails(payload);
      setWipReelDetails(data);
    } catch (error) {
      console.error("Failed to load data without date range:", error);
      setWipReelDetails([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dayjs(filterFromDate).isValid() && dayjs(filterToDate).isValid()) {
      loadDataWithDate(filterFromDate, filterToDate);
    }
  }, [filterFromDate, filterToDate]);
  

  const handleFilterFromDate = (date) => {
    setFilterFromDate(dayjs(date));
  };

  const handleFilterToDate = (date) => {
    setFilterToDate(dayjs(date));
  };

  const handleFilterChange = (e, column) => {
    setFilters({ ...filters, [column]: e.target.value });
  };

  const filteredData = wipReelDetails.filter((row) => {
    return Object.keys(filters).every((key) => {
      if (filters[key] === "") return true;
      return String(row[key] || "").toLowerCase().includes(filters[key].toLowerCase());
    });
  });

  const pageCount = Math.ceil(filteredData.length / recordsPerPage);
  const currentRecords = filteredData.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleExportToExcel = () => {
    const formattedData = filteredData.map((row) => ({
      "PROD. DATE": dayjs(row.prod_date).format("DD-MM-YYYY"),
      SHIFT_CODE: row.shift_code || "",
      "ROLL NO": row.roll_no,
      "REEL NO": row.reel_no,
      QUALITY: row.quality,
      GRADE: row.grade,
      GSM: row.gsm,
      REEL: row.size,
      WEIGHT: row.weight,
     "ORDER NO.": row.po_no,
      REMARKS: row.remarks,
      "PARTY REMARKS": row.party_remarks,
    }));
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "WIP REEL DETAILS");
    const fileName = `WIP REEL DETAILS.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const handleCallBack = () => {
    handleBackWinderMachineEntry();
  };

  const uniqueValues = {
    quality: [...new Set(wipReelDetails.map((row) => row.quality))],
    grade: [...new Set(wipReelDetails.map((row) => row.grade))],
    shift_code: [...new Set(wipReelDetails.map((row) => row.shift_code))],
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
          p: 2,
        }}
      >
        <Divider />
        <Grid container padding="5px 0px" alignItems="center" sx={{ justifyContent: "flex-end" }}>
          <Box marginRight="auto" marginLeft={1}>
            <Typography variant="h4" color={colors.grey[100]} fontWeight="semi bold">
              View WIP Reel Details
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* From Date */}
          <Grid item xs={2} sm={3} md={1.5} lg={1.5} xl={1.2}>
            <FormControl fullWidth size="small">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  id="FromDate"
                  value={dayjs(filterFromDate)}
                  onChange={(date) => handleFilterFromDate(date)}
                  format="DD-MMM-YYYY"
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>

          <Grid item mt="2px" ml="10px" mr="10px">
            <Typography fontWeight="bold">-</Typography>
          </Grid>

          {/* To Date */}
          <Grid item xs={2} sm={3} md={1.5} lg={1.5} xl={1.2}>
            <FormControl fullWidth size="small">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  id="ToDate"
                  value={dayjs(filterToDate)}
                  onChange={(date) => handleFilterToDate(date)}
                  format="DD-MMM-YYYY"
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <Grid item mt="2px" ml="5px" mr="5px">
            <Button size="small" variant="contained" color="secondary" onClick={loadDataWithoutDate}>
              Fetch Data Without Date Range
            </Button>
          </Grid>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <Grid item mt="2px" ml="5px" mr="5px">
            <IconButton onClick={handleExportToExcel}>
              <DownloadIcon />
            </IconButton>
          </Grid>

          <Divider orientation="vertical" flexItem />
          {mode === 0 && (
            <>
              <Grid item mt="2px" ml="5px" mr="5px" justifyContent="center">
                <IconButton onClick={handleCallBack}>
                  <Close style={{ fontSize: "14px", color: "#2196F3" }} />
                </IconButton>
              </Grid>
              <Divider orientation="vertical" flexItem />
            </>
          )}
        </Grid>

        <Divider />
        <CustomTableContainer colors={colors}>
          <TableContainer sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {loading ? (
              <SkeletonTableLoader rows={10} columns={6} />
            ) : (
              <Table size="small">
                <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                  <TableRow>
                    <TableCell sx={{ textAlign: "center" }}>PROD. DATE</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>SHIFT</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>ROLL NO</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>REEL NO</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>QUALITY</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>GRADE</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>GSM</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>REEL</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>WEIGHT</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>ORDER NO.</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>REMARKS</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>PARTY REMARKS</TableCell>
                  </TableRow>

                  {/* Filter Row */}
                  <TableRow>
                    {[
                      "prod_date",
                      "shift_code",
                      "roll_no",
                      "reel_no",
                      "quality",
                      "grade",
                      "gsm",
                      "size",
                      "weight",
                      "po_no",
                      "remarks",
                      "party_remarks",
                    ].map((col) => (
                      <TableCell key={col} sx={{ padding: "4px" }}>
                        {["quality", "grade", "shift_code"].includes(col) ? (
                          <Autocomplete
                            freeSolo
                            options={uniqueValues[col] || []}
                            value={filters[col] || ""}
                            onInputChange={(event, newValue) =>
                              handleFilterChange({ target: { value: newValue } }, col)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Filter"
                                variant="standard"
                                InputProps={{
                                  ...params.InputProps,
                                  disableUnderline: true,
                                }}
                                sx={{ fontSize: "0.75rem", borderRadius: 1, px: 1 }}
                              />
                            )}
                          />
                        ) : (
                          <TextField
                            variant="standard"
                            value={filters[col] || ""}
                            onChange={(e) => handleFilterChange(e, col)}
                            placeholder="Filter"
                            fullWidth
                            InputProps={{
                              disableUnderline: true,
                            }}
                            sx={{ fontSize: "0.75rem", borderRadius: 1, px: 1 }}
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {currentRecords.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor:
                          index % 2 === 0
                            ? theme.palette.background.default
                            : alpha(theme.palette.action.hover, 0.04),
                      }}
                    >
                      {/* PROD. DATE */}
      {/* <TableCell sx={{ textAlign: "center" }}>
        {dayjs(row.prod_date).format("DD-MM-YYYY")}
      </TableCell> */}
      <TableCell sx={{ textAlign: "center" }}>
  {row?.prod_date
    ? dayjs(row.prod_date).format("DD-MMM-YYYY")
    : ""}
</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>{row.shift_code}</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>{row.roll_no}</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>{row.reel_no}</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>{row.quality}</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>{row.grade}</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>{row.gsm}</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>{row.size}</TableCell>
                      <TableCell sx={{ textAlign: "right" }}>{row.weight}</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>{row.po_no}</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>{row.remarks}</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>{row.party_remarks}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <CustomPagination pageCount={pageCount} currentPage={currentPage} onPageChange={handlePageChange} />
          </TableContainer>
        </CustomTableContainer>
      </Box>
    </>
  );
};

export default ReportWinderProuctionWIPReelsList;
